import { createStandaloneToast } from '@chakra-ui/react';
import axios from 'axios';
import { TEMPORARY } from 'helpers/constant';
import { TASK_FILE_STATUS, TASK_STATUS } from 'helpers/enum';
import { RootState } from 'helpers/store';
import { SummaryService } from 'services/summary/summary.service';
import { call, put, select, takeEvery } from 'typed-redux-saga';
import {
  AddNewQuestionAction,
  DeleteTaskAction,
  EditQuestionAction,
  EditTaskNameAction,
  GetDetailTaskAction,
  GetTasksAction,
  UpdateFileStatusAction,
} from 'types/actions/task';

import { taskAction } from '../actions';
import { taskConstant } from '../actionTypes';

const { toast } = createStandaloneToast();

const getTasksData = function* ({ payload }: GetTasksAction) {
  try {
    const result = yield* call(() => {
      return SummaryService.getTasks(payload);
    });
    const state = yield* select((state: RootState) => state.task);
    const tasks = state?.tasks;
    const newTasks = { ...result };

    const temporaryTasks = tasks.data.filter((task) => task.type === TEMPORARY);

    if (temporaryTasks && payload?.isKeepTemporaryTask) {
      newTasks.data = [...temporaryTasks, ...newTasks.data];
    }

    if (payload?.page) {
      newTasks.data = [...tasks.data, ...result.data];
    }

    if (payload?.isKeepCurrentState) {
      yield* put(
        taskAction.updateTasks({
          ...newTasks,
          current_page: payload?.page ?? 0,
        })
      );
      return;
    }

    yield* put(
      taskAction.updateTasks({
        ...newTasks,
        current_page: payload?.page ?? 0,
      })
    );
  } catch (error) {
    console.log(error);
  }
};

const getDetailTaskData = function* ({ payload }: GetDetailTaskAction) {
  console.log(payload);
  if (!payload.task_id) {
    return;
  }
  try {
    const isTemporaryTask = payload?.type === TEMPORARY;
    if (isTemporaryTask) {
      // Clear current select files and load from cache
      yield* put(taskAction.updateCurrentTaskData([]));
      const state = yield* select((state: RootState) => state.task);

      if (payload.task_id === state.temporary_task.task_id) {
        yield* put(taskAction.loadTemporaryTaskData());
      }
    } else {
      yield* put(taskAction.updateStatus(TASK_STATUS.LOADING));
      const result = yield* call(() => {
        return SummaryService.getDetailTask({
          id: payload.task_id,
        });
      });

      if (payload.isWaitingProcessFileUpload === false) {
        yield* put(taskAction.clearCurrentTaskSelectedFiles());
        yield* put(taskAction.updateCurrentTaskData(result));
      } else {
        yield* put(taskAction.updateCurrentTaskData(result));
        if (result.length === 0) {
          for (const file of payload.selectedFiles) {
            yield* put(
              taskAction.addSelectedFile({ ...file, task_file_status: TASK_FILE_STATUS.UPLOADED })
            );
          }
        }
      }

      // Get latest message to set default model
      try {
        const latestMessage = yield* call(() => {
          return SummaryService.getLatestMessageTask({
            id: payload.task_id!,
          });
        });

        if (latestMessage?.requested_model) {
          // Get models from store to map requested_model to model id
          const models = yield* select((state: RootState) => state.model.models);
          const model = models.find(
            (m: any) =>
              m.model_name === latestMessage.requested_model ||
              m.name === latestMessage.requested_model ||
              m.id === latestMessage.requested_model
          );

          if (model) {
            yield* put(
              taskAction.updateCurrentTask({
                task_id: payload.task_id,
                type: payload.type,
                selectedModelId: model.id,
              })
            );
          }
        }
      } catch (error) {
        console.error('Failed to get latest message for default model:', error);
      }

      yield* put(taskAction.updateStatus(TASK_STATUS.IDLE));
    }

    // Store current selected files to detect files change when switch task
    yield* put(taskAction.updateInitialSelectedFiles());
  } catch (error) {
    console.log(error);
  }
};

const editTaskName = function* ({ payload }: EditTaskNameAction) {
  const state = yield* select((state: RootState) => state.task);
  const editTask = state.tasks.data.find((task) => task.id === payload.id);
  const isTemporaryTask = editTask?.type === TEMPORARY;
  const isCurrentTask = payload.id === state.current.task_id;

  // Not allow to edit name temporary task
  if (isTemporaryTask) {
    return;
  }

  const result = yield* call(() => {
    return SummaryService.editTaskName(payload);
  });
  if (isCurrentTask) {
    const newTasks = state.tasks;
    newTasks.data = newTasks.data.filter((task) => task.id !== payload.id);

    yield* put(taskAction.updateTasks(newTasks));
  }
  toast({
    title: `Renamed to ${result.name}`,
    status: 'success',
    position: 'bottom-right',
    isClosable: true,
  });
  yield* put(taskAction.getTasks({ isKeepTemporaryTask: true }));
};

const deleteTask = function* ({ payload }: DeleteTaskAction) {
  const state = yield* select((state: RootState) => state.task);
  const deleteTaskState = state.tasks.data.find((task) => task.id === payload.id);

  const isTemporaryTask = deleteTaskState?.type === TEMPORARY;
  const isCurrentTask = payload.id === state.current.task_id;

  if (!isTemporaryTask) {
    yield* call(() => {
      return SummaryService.deleteTask(payload);
    });
  }
  if (isCurrentTask) {
    const newTasks = state.tasks;
    newTasks.data = newTasks.data.filter((task) => task.id !== payload.id);

    yield* put(taskAction.updateTasks(newTasks));
    yield* put(taskAction.updateCurrentTask({ task_id: null, type: '', selectedFiles: [] }));
    yield* put(taskAction.updateInitialSelectedFiles());
  }
  yield* put(taskAction.getTasks({ isKeepTemporaryTask: true }));
};

const updateFileStatus = function* ({ payload }: UpdateFileStatusAction) {
  const state = yield* select((state: RootState) => state.task);
  const isTemporaryTask = !payload.summaryId;

  // Processed upload file for new task
  if (isTemporaryTask || payload.summaryId === state.current.task_id) {
    yield* put(
      taskAction.updateSelectedFile({
        data: { ...payload, task_file_status: TASK_FILE_STATUS.PROCESSED },
        isTemporary: isTemporaryTask,
      })
    );
    yield* put(taskAction.updateCurrentTask({ isWaitingProcessFileUpload: false }));
  }
};

const addNewQuestion = function* ({ payload }: AddNewQuestionAction) {
  const state = yield* select((state: RootState) => state.task);
  const selectedFilesId = state.current.selectedFiles.map((file) => file.id);
  const isCurrentTemporaryTask = state?.current?.type === TEMPORARY;
  try {
    yield* put(taskAction.updateNewQuestion({ ...payload, status: 'loading' }));

    const result = yield* call(() => {
      // payload.model.name contains the model_name (set in Chat.tsx)
      const modelName = payload?.model?.name || undefined;
      return SummaryService.addQuestion({
        question: payload?.question,
        fileIds: selectedFilesId,
        requested_model: modelName, // Send model_name to backend
        ...(!isCurrentTemporaryTask && {
          id: state?.current?.task_id,
        }),
      });
    });

    if (isCurrentTemporaryTask) {
      // Refetch all tasks to get task data after created
      yield* put(taskAction.getTasks());
      yield* put(
        taskAction.updateCurrentTask({
          task_id: result.id,
          type: '',
        })
      );
      yield* put(taskAction.updateProcessTaskId(result.id));
    }
    yield* put(taskAction.updateInitialSelectedFiles());
  } catch (error) {
    if (state?.current?.task_id) {
      yield* put(
        taskAction.updateNewAnswer({
          status: 'error',
          id: state.current.task_id,
        })
      );
    }

    if (axios.isAxiosError(error) && error.response?.data?.error_message) {
      if (
        error?.response?.data?.error_message ===
        'Exceeded maximum number of question per organization'
      ) {
        yield* put(taskAction.updateLimitStatus(true));
      }
      toast({
        title: error?.response?.data?.error_message,
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  }
};

const editQuestion = function* ({ payload }: EditQuestionAction) {
  const state = yield* select((state: RootState) => state.task);
  const selectedFilesId = state.current.selectedFiles.map((file) => file.id);
  try {
    // Fetch models to get model_name for the selected model
    let modelName: string | undefined;
    if (state?.current?.selectedModelId) {
      try {
        const models = yield* select((state: RootState) => state.model.models);
        const selectedModel = models.find((m: any) => m.id === state?.current?.selectedModelId);
        modelName = selectedModel?.model_name || selectedModel?.name || undefined;
      } catch (error) {
        console.error('Failed to fetch models for editQuestion:', error);
      }
    }

    yield* put(taskAction.updateCurrentQuestion({ ...payload, requested_model: modelName }));
    yield* put(taskAction.updateStatus(TASK_STATUS.GENERATE_ANSWER));
    if (state.current.task_id) {
      yield* put(taskAction.updateProcessTaskId(state.current.task_id));
    }

    yield* call(() => {
      return SummaryService.editQuestion({
        question: payload?.question,
        fileIds: selectedFilesId,
        requested_model: modelName, // Send model_name to backend
        id: state?.current?.task_id,
      });
    });
  } catch (error) {
    if (state?.current?.task_id) {
      yield* put(
        taskAction.updateNewAnswer({
          status: 'error',
          id: state.current.task_id,
        })
      );
    }
    yield* put(taskAction.updateStatus(TASK_STATUS.IDLE));

    if (axios.isAxiosError(error) && error?.response?.data?.error_message) {
      if (
        error?.response?.data?.error_message ===
        'Exceeded maximum number of question per organization'
      ) {
        yield* put(taskAction.updateLimitStatus(true));
      }
      toast({
        title: error?.response?.data?.error_message,
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  }
};

export const watchGetTasks = function* () {
  yield* takeEvery(taskConstant.GET_TASKS, getTasksData);
};

export const watchEditTaskName = function* () {
  yield* takeEvery(taskConstant.EDIT_TASK_NAME, editTaskName);
};

export const watchGetDetailTask = function* () {
  yield* takeEvery(taskConstant.GET_DETAIL_TASK, getDetailTaskData);
};

export const watchAskQuestion = function* () {
  yield* takeEvery(taskConstant.ADD_NEW_QUESTION_TASK, addNewQuestion);
};

export const watchEditQuestion = function* () {
  yield* takeEvery(taskConstant.EDIT_QUESTION_TASK, editQuestion);
};

export const watchDeleteTask = function* () {
  yield* takeEvery(taskConstant.DELETE_TASK, deleteTask);
};

export const watchUpdateFileStatus = function* () {
  yield* takeEvery(taskConstant.UPDATE_FILE_STATUS, updateFileStatus);
};
